import React, { useState, useEffect } from "react";
import { 
  Calculator, Target, GraduationCap, Users, Globe, Clock, AlertCircle, 
  BookOpen, TrendingUp, ChevronRight 
} from "lucide-react";
import { motion } from "framer-motion";
import ContactForm from "../components/ContactForm";
import { Link } from "react-router-dom";

const GREPage = () => {
  const [verbalScore, setVerbalScore] = useState(155);
  const [quantScore, setQuantScore] = useState(160);
  const [writingScore, setWritingScore] = useState(4.0);
  const [totalScore, setTotalScore] = useState(315);
  const [scoreProfile, setScoreProfile] = useState("Balanced Score Range");

  const updateScore = () => {
    const total = verbalScore + quantScore;
    setTotalScore(total);
    
    if (quantScore >= 160 && verbalScore >= 152) {
      setScoreProfile("Quant-Strong Profile (STEM-aligned)");
    } else if (verbalScore >= 158 && quantScore >= 152) {
      setScoreProfile("Verbal-Strong Profile (Humanities-aligned)");
    } else if (total >= 310) {
      setScoreProfile("Balanced Score Range");
    } else {
      setScoreProfile("Below Typical Admitted Range");
    }
  };

  useEffect(() => {
    updateScore();
  }, [verbalScore, quantScore, writingScore]);

  const selfSelectCards = [
    { icon: GraduationCap, title: "STEM/Quantitative Programs", desc: "Engineering, Data Science, Economics where Quant scores matter most" },
    { icon: Users, title: "Competitive Research Programs", desc: "PhD/MS programs where test scores provide clear admissions edge" },
    { icon: TrendingUp, title: "Scholarship Applicants", desc: "GRE strengthens merit aid at test-optional programs (median+ scores only)" }
  ];

  const benchmarks = [
    { score: "300–310", meaning: "Foundation level", unis: "Pathway programs, less competitive MS programs" },
    { score: "315–320", meaning: "Competitive range", unis: "Strong STEM/non-STEM master's programs" },
    { score: "325+", meaning: "Research-competitive", unis: "Typical range for top-tier research programs (varies by field)" }
  ];

  const myths = [
    { text: "GRE required for all MS programs", counter: "Many test-optional. Check specific program requirements" },
    { text: "High GRE compensates for low GPA", counter: "GPA/SOP/LORs primary. GRE is supporting evidence only" },
    { text: "GRE no longer matters", counter: "Strategic for competitive programs and scholarships" },
    { text: "Verbal irrelevant for STEM", counter: "Quant weighted higher, but Verbal still reviewed" }
  ];

  const testFormat = [
    { section: "Verbal Reasoning", duration: "41 min (2 sections, adaptive)" },
    { section: "Quantitative Reasoning", duration: "47 min (2 sections, adaptive)" },
    { section: "Analytical Writing", duration: "30 min (1 essay task)" }
  ];

  const preparationFlow = [
    { step: "Diagnostic Profiling", desc: "Identify Quant/Verbal gaps vs target program medians + AW readiness" },
    { step: "Section-Adaptive Strategy", desc: "Early section accuracy disproportionately impacts final scores" },
    { step: "Advanced Quant Reasoning", desc: "Data interpretation, logical equivalence, time-pressured problem-solving" },
    { step: "Full-Length Simulations", desc: "2-hour adaptive GRE with official 130-170 scoring + AW evaluation" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
      {/* 1. Hero + Calculator */}
      <section className="py-24 px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-light text-slate-900 leading-tight">
              GRE General Test Overview
            </h1>
            <p className="text-xl text-slate-600 max-w-lg leading-relaxed">
              Graduate Record Examination for master's and PhD admissions worldwide. Tests Verbal, Quantitative Reasoning, and Analytical Writing.
            </p>
            <p className="text-lg text-slate-500">
              130-170 per section (260-340 total) + 0-6 Writing. GRE helps only when it clearly strengthens your profile; mediocre scores can disadvantage test-optional applicants.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 shadow-xl border border-slate-100 max-h-[32rem] overflow-y-auto"
          >
            <div className="flex items-center gap-3 mb-4">
              <Calculator className="w-6 h-6 text-emerald-600" />
              <h3 className="text-xl font-semibold text-slate-900">Estimate Your GRE Scores</h3>
            </div>
            <p className="text-xs text-slate-500 mb-4 italic">
              Section importance varies by program. Indicative only. Actual competitiveness depends on GPA, coursework, research, program expectations.
            </p>
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Verbal Reasoning (130-170)</label>
                <input
                  type="range"
                  min="130" max="170"
                  value={verbalScore}
                  onChange={e => setVerbalScore(+e.target.value)}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-600"
                />
                <span className="text-sm font-mono text-slate-600">V: {verbalScore}</span>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Quantitative Reasoning (130-170)</label>
                <input
                  type="range"
                  min="130" max="170"
                  step="1"
                  value={quantScore}
                  onChange={e => setQuantScore(+e.target.value)}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-600"
                />
                <span className="text-sm font-mono text-slate-600">Q: {quantScore}</span>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-slate-700">Analytical Writing (0-6)</label>
                <input
                  type="range"
                  min="0" max="6"
                  step="0.5"
                  value={writingScore}
                  onChange={e => setWritingScore(+e.target.value)}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-emerald-500 hover:accent-emerald-600"
                />
                <span className="text-sm font-mono text-slate-600">AW: {writingScore}</span>
              </div>
              <div className="pt-6 border-t border-slate-200">
                <div className="text-3xl font-bold text-slate-900 mb-1">Total: {totalScore} (V+Q)</div>
                <div className="text-sm font-semibold text-emerald-700 mb-1">{scoreProfile}</div>
                <p className="text-xs text-slate-500">AW {writingScore} ({writingScore >= 4 ? "Competitive" : "Review needed"})</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Who Should Take GRE */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Who Should Take GRE?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            GRE strategic when required/recommended AND scores will be above program medians. Strong GPA applicants to test-optional programs may not benefit.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {selfSelectCards.map((card, i) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className="group bg-white rounded-3xl p-10 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 border border-slate-100 hover:border-emerald-200"
            >
              <card.icon className="w-12 h-12 text-emerald-600 mb-6 mx-auto" />
              <h3 className="text-2xl font-semibold text-slate-900 mb-4">{card.title}</h3>
              <p className="text-slate-600 leading-relaxed">{card.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3. CTA Cluster */}
      <section className="py-24 px-6 md:px-12 bg-emerald-50 border-t border-emerald-200">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <div className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-2xl shadow-lg">
            <BookOpen className="w-8 h-8 text-emerald-600" />
            <div>
              <h3 className="text-xl font-semibold text-slate-900">Next Steps</h3>
              <p className="text-sm text-slate-600">Review GRE strategy for your target programs</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/test-prep#gre">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="bg-emerald-600 hover:bg-emerald-700 px-8 py-4 rounded-2xl text-lg font-semibold shadow-xl flex items-center gap-2"
            >
              GRE Preparation Programs
              <ChevronRight className="w-5 h-5" />
            </motion.button>
            </Link>
            <Link to="/test-prep">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="border-2 border-slate-600 hover:border-slate-400 bg-white text-slate-900 px-8 py-4 rounded-2xl text-lg font-semibold flex items-center gap-2"
            >
              All Test Prep
            </motion.button>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. Score Benchmarks */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            What Your Scores Mean
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
            Admissions holistic: GPA/SOP/LORs primary. Submitting GRE when optional signals confidence — but only if scores align with program medians.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {benchmarks.map((bench, i) => (
            <motion.div
              key={bench.score}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 hover:shadow-xl border border-slate-100"
            >
              <div className="text-3xl font-bold text-emerald-600 mb-4">{bench.score}</div>
              <h4 className="text-xl font-semibold text-slate-900 mb-3">{bench.meaning}</h4>
              <p className="text-slate-600">{bench.unis}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 5. Institutional Acceptance */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Institutional Acceptance
          </h2>
          <p className="text-xl text-slate-600">
            Widely accepted but increasingly test-optional. Test-optional ≠ test-blind.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-3xl p-10">
            <Globe className="w-16 h-16 text-emerald-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">University Admissions</h3>
            <p className="text-lg text-slate-600">Required: Many engineering PhD programs. Optional: Most US master's. Always verify program requirements.</p>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-10">
            <Target className="w-16 h-16 text-blue-600 mx-auto mb-6" />
            <h3 className="text-2xl font-semibold text-slate-900 mb-4">Strategic Value</h3>
            <p className="text-lg text-slate-600">Demonstrates academic readiness + strengthens scholarships at test-optional programs.</p>
          </motion.div>
        </div>
      </section>

      {/* 6. Common Misconceptions */}
      <section className="py-24 px-6 md:px-12 bg-slate-50">
        <div className="max-w-4xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-12 text-center">
            Common Misconceptions
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {myths.map((myth, i) => (
              <motion.div
                key={myth.text}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="flex flex-col gap-4 p-6 bg-white rounded-2xl border border-slate-200 hover:border-amber-200 group"
              >
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-8 h-8 text-amber-500 flex-shrink-0 mt-1" />
                  <p className="text-slate-700 group-hover:text-slate-900 font-medium">{myth.text}</p>
                </div>
                <p className="text-sm text-slate-600 font-semibold bg-amber-50 p-3 rounded-xl">
                  {myth.counter}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Test Structure */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-2xl mx-auto text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Test Structure
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-16">
            Total: ~2 hours (shorter format). Section-adaptive format means early accuracy disproportionately impacts final scores.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
          {testFormat.map((format, i) => (
            <motion.div
              key={format.section}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-8 text-center border border-slate-100 hover:shadow-lg"
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-4">{format.section}</h3>
              <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <div className="text-2xl font-bold text-slate-900">{format.duration}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 8. How We Help You Prepare */}
      <section className="py-24 px-6 md:px-12 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            How We Help You Prepare
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Strategic GRE preparation aligned with program-specific score expectations and adaptive testing algorithms.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {preparationFlow.map((step, i) => (
            <motion.div
              key={step.step}
              initial={{ opacity: 0, x: i % 2 ? 20 : -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-10 border border-slate-100 hover:shadow-xl hover:border-emerald-200"
            >
              <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-emerald-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-emerald-700">{i + 1}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-slate-900">{step.step}</h3>
                </div>
              </div>
              <p className="text-slate-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 9. ContactForm */}
      <section id="contact-form" className="py-24 px-6 md:px-12 max-w-4xl mx-auto bg-slate-50">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif font-light text-slate-900 mb-6">
            Questions About GRE?
          </h2>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Program-specific GRE requirements change yearly. Schedule consultation for your target programs.
          </p>
        </div>
        <ContactForm />
      </section>
    </div>
  );
};

export default GREPage;
